<template>
    <section v-if="!Utils.isObjectEmpty(factoringCompany)">
        <sui-container fluid>
            <sui-form v-if="editField" @submit.prevent="" :error="!!errors.length" basic>
                <sui-message error>
                    <sui-message-header>Errores</sui-message-header>
                    <sui-message-list>
                        <sui-message-item v-for="(error, index) in errors" :key="index">{{ error }}</sui-message-item>
                    </sui-message-list>
                </sui-message>
                <sui-message color="red" v-if="record.statusId === FactoringCompanyStatus.REJECTED" class="mb-10">
                    <sui-message-header>¡Importante!</sui-message-header>
                    <p>
                        Las relaciones comerciales en empresas <b>RECHAZADAS</b> se muestran solo de manera informativa, ya que <b>NO</b> tienen efecto en las operaciones de factoraje.
                    </p>
                </sui-message>
                <sui-form-fields fields="two" v-if="record.statusId !== FactoringCompanyStatus.REJECTED">
                    <sui-form-field>
                        <label>Buscar clientes</label>
                        <multi-select
                            placeholder="Buscar"
                            selectLabel="Enter ↵ para agregar"
                            @select="addClient"
                            @search-change="searchClients"
                            :options="availableClients"
                            :searchable="true"
                            :loading="loadingClients"
                            :internal-search="false"
                            :clear-on-select="false"
                            :close-on-select="false"
                            :optionsLimit="100"
                            :hide-selected="true"
                            track-by="clientId"
                            label="entityName"
                        >
                            <template slot="noOptions">
                                Busque clientes por nombre o RFC
                            </template>
                            <template slot="noResult" slot-scope="{ search }">
                                <span v-if="search?.length < 3">Ingrese al menos 3 caracteres</span>
                                <h4 v-else class="ui red header">No se encontraron registros</h4>
                            </template>
                        </multi-select>
                    </sui-form-field>
                    <sui-form-field>
                        <label>&nbsp;</label>
                        <sui-button primary content="Guardar" @click="save()"/>
                        <sui-button v-if="dirty" default content="Descartar cambios" @click="reset()"/>
                    </sui-form-field>
                </sui-form-fields>
            </sui-form>
        </sui-container>
        <sui-container fluid>
            <UnPaginatedList :records="relatedClientFactoringCompanies || []" :headers="headers.filter(_ => _.value !== 'actions' || editField)" :loading="loadingClients" :empty-text="'No hay empresas agregadas'" :edit-field="editField">
                <template v-slot:rfc="{ item }">
                    {{ item.rfc }}
                </template>
                <template v-slot:plazoAcordado="{ item }">
                    <span v-if="item.exclude">-</span>
                    <sui-input v-else-if="editField && !item.exclude && record.statusId !== FactoringCompanyStatus.REJECTED" v-model="item.plazoAcordado" :class="{'error': !item.plazoAcordado || item.plazoAcordado < 10}" placeholder="días" />
                    <span v-else>{{ item.plazoAcordado || 'N/A' }}</span>
                </template>
                <template v-slot:tasaAdicional="{ item }">
                    <span v-if="item.exclude">-</span>
                    <sui-input v-else-if="editField && !item.exclude && record.statusId !== FactoringCompanyStatus.REJECTED && fullAdministration" class="full-input" :class="{'error': item.tasaAdicional < -5 }" type="number" max="100" min="-5" v-model="item.tasaAdicional" placeholder="Tasa adicional" />
                    <span v-else>{{ item.tasaAdicional || 0 }}%</span>
                </template>
                <template v-slot:multiplicadorMoratorio="{ item }">
                    <span v-if="item.exclude">-</span>
                    <sui-input v-else-if="editField && !item.exclude && record.statusId !== FactoringCompanyStatus.REJECTED && fullAdministration" class="full-input" :class="{'error': item.multiplicadorMoratorio <= 0 }" type="number" max="3" min="1" v-model="item.multiplicadorMoratorio" placeholder="Mutiplicador" />
                    <span v-else>{{ item.multiplicadorMoratorio || 1.3 }}</span>
                </template>
                <template v-slot:porcentajeAdelanto="{ item }">
                    <span v-if="item.exclude">-</span>
                    <sui-input v-else-if="editField && !item.exclude && record.statusId !== FactoringCompanyStatus.REJECTED && fullAdministration" class="full-input" type="number" max="100" min="1" v-model="item.porcentajeAdelanto" :class="{'error': !item.porcentajeAdelanto || item.porcentajeAdelanto < 1 || item.porcentajeAdelanto > 100 }" placeholder="% Adelanto" />
                    <span v-else>{{ item.porcentajeAdelanto || 0 }}%</span>
                </template>
                <template v-slot:exclude="{ item }">
                    <sui-checkbox :disabled="!editField || record.statusId === FactoringCompanyStatus.REJECTED" toggle v-model="item.exclude" />
                </template>
                <template v-slot:actions="{ item }" v-if="record.statusId !== FactoringCompanyStatus.REJECTED">
                    <sui-button v-if="editField && item.id" @click="removeClient(item)" content="Eliminar" icon="trash" label-position="left" danger size="tiny" color="red"/>
                    <sui-button v-if="editField && !item.id" @click="removeClient(item)" content="Eliminar" icon="trash" label-position="left" danger size="tiny" color="red"/>
                </template>
            </UnPaginatedList>
        </sui-container>
    </section>
</template>

<script>
import Mixins from '@/mixins/index.js'
// import BaseService from '@/services/BaseService'
// import ClientViewService from '@/services/ClientViewService'
// import CreditsManagerService from '@/services/CreditsManagerService'
import ClientsManagerService from '@/services/ClientsManagerService'
import { useDebounceFn } from '@vueuse/core'
import FactoringCompanyStatus from '@/common/FactoringCompanyStatus'
// import FactoringCompanyTypes from '@/common/FactoringCompanyTypes'
import UnPaginatedList from '@/components/Generic/UnpaginatedList.vue'

export default {
    name: 'ClientFactoringCompany',
    emits: ['loadRecord'],
    mixins: [Mixins],
    props: {
        factoringCompany: {
            type: Object, default: () => {}
        },
        companyId: {
            type: Number, default: () => null
        },
        dirty: {
            type: Boolean, default: false
        }
    },
    components: { UnPaginatedList },
    data () {
        return {
            apiEndpoint: '',
            loadingClients: false,
            errors: [],
            clients: [],
            clientFactoringCompaniesRelations: [],
            clientFactoringCompaniesRelationsCount: 0,
            relatedClientFactoringCompanies: null,
            headers: [
                { name: 'RFC', value: 'rfc', type: 'slot', slotName: 'rfc' },
                { name: 'Cliente', value: 'entityName' },
                { name: 'Plazo de días', value: 'plazoAcordado', type: 'slot', slotName: 'plazoAcordado', cellclass: 'txtc', class: 'txtc', tooltip: 'Representa el plazo máximo de dias permitidos con el dragón desde la fecha de emisión hasta la fecha de promesa de pago' },
                { name: 'Tasa adicional', value: 'tasaAdicional', type: 'slot', slotName: 'tasaAdicional', cellclass: 'txtc', class: 'txtc', tooltip: 'Representa los puntos adicionales que serán aplicados a la tasa ordinaria en las transacciones con el dragón' },
                { name: 'Multiplicador para Moratorios', value: 'multiplicadorMoratorio', type: 'slot', slotName: 'multiplicadorMoratorio', cellclass: 'txtc', class: 'txtc', tooltip: 'Representa el multiplicador que se aplica sobre la tasa ordinaria para establecer la tasa moratoria aplicable' },
                { name: 'Porcentaje de adelanto', value: 'porcentajeAdelanto', type: 'slot', slotName: 'porcentajeAdelanto', cellclass: 'txtc', class: 'txtc', tooltip: 'Representa el adelanto que se hará en las transacciones con el dragón' },
                { name: 'Restringir', value: 'exclude', type: 'slot', slotName: 'exclude', cellclass: 'txtc', class: 'txtc', tooltip: 'Si se restringe el cliente no podrá realizar transacciones con este dragón' },
                { name: 'Acciones', value: 'actions', type: 'slot', slotName: 'actions', cellclass: 'txtc', class: 'txtc' }
            ],
            canManageClientFactoringCompanyRelations: false,
            FactoringCompanyStatus
        }
    },
    async mounted () {
        this.canManageClientFactoringCompanyRelations = await this.userHasPerm('can_manage_client_factoring_company_relations')
        await this.setRelations()
    },
    methods: {
        gotDirty (state) {
            this.$emit('update:dirty', state)
        },

        async reset () {
            this.relatedClientFactoringCompanies = null
            this.errors = false
            await this.setRelations()
        },

        async setRelations () {
            let loader = this.$loading.show()
            try {
                await this.loadRecordsCM2(null, '/client-factoring-company', 'clientFactoringCompaniesRelations', 'clientFactoringCompaniesRelationsCount', { active: true, factoringCompanyId: this.companyId })

                const clients = this.clientFactoringCompaniesRelationsCount ? await ClientsManagerService.getAll('/api/clients', { clientId: this.clientFactoringCompaniesRelations.map(_ => _.clientId) }) : []
                this.relatedClientFactoringCompanies = clients.map(item => {
                    const relation = this.clientFactoringCompaniesRelations.find(_ => _.clientId === item.clientId)
                    return {
                        ...relation,
                        rfc: item.rfc,
                        entityName: item.entityName
                        // clientId: relation.clientId,
                        // factoringCompanyId: relation.factoringCompanyId,
                        // days: relation && relation.plazoAcordado
                        // tasaAdicional: relation && relation.tasaAdicional,
                        // porcentajeAdelanto: relation && relation.porcentajeAdelanto,
                        // exclude: relation && relation.exclude
                    }
                })
                this.gotDirty(false)
            } catch (error) {
                console.error(error)
                this.$swal('Error', 'Error al consultar las relaciónes existentes', 'error')
            } finally {
                loader.hide()
            }
        },

        searchClients: useDebounceFn(async function (searchQuery) {
            if (!searchQuery || searchQuery.length < 3) {
                this.clients = []
                return
            }

            this.loadingClients = true
            await this.loadRecords(null, '/v2/client', 'clients', 'clientsCount', { name: searchQuery, rfc: searchQuery })
            this.loadingClients = false
        }),

        async addClient (client) {
            if (this.relatedClientFactoringCompanies.some(item => item.clientId === client.clientId)) {
                this.$swal('Advertencia', 'El cliente ya está agregado', 'warning')
                return
            }

            this.relatedClientFactoringCompanies.push({
                clientId: client.clientId,
                factoringCompanyId: this.companyId,
                creditLineId: client.creditLineId,
                reservedFolioId: client.reservedFolioId,
                rfc: client.rfc,
                entityName: client.entityName,
                plazoAcordado: 0,
                tasaAdicional: 0,
                multiplicadorMoratorio: client.multiplicadorMoratorio,
                porcentajeAdelanto: client.porcentajeAdelanto,
                exclude: false,
                active: true
            })
        },

        async removeClient (relation) {
            if (relation.id) {
                relation.active = false
            } else {
                this.relatedClientFactoringCompanies = this.relatedClientFactoringCompanies.filter(item => item.clientId !== relation.clientId)
            }
        },

        async save () {
            this.errors = []
            if (this.relatedClientFactoringCompanies.some(item => !item.exclude && (!item.plazoAcordado || item.plazoAcordado <= 0))) this.errors.push('Todos los plazos deben ser mayores a 0')
            if (this.relatedClientFactoringCompanies.some(item => !item.exclude && (item.tasaAdicional === null))) this.errors.push('Especifica las tasas adicionales')
            if (this.relatedClientFactoringCompanies.some(item => !item.exclude && (item.tasaAdicional < -5))) this.errors.push('Las tasas adicionales no pueden disminuirse mas de 5 puntos')
            if (this.relatedClientFactoringCompanies.some(item => !item.exclude && (item.multiplicadorMoratorio <= 0))) this.errors.push('Los multiplicadores para moratorios no pueden ser 0')
            if (this.relatedClientFactoringCompanies.some(item => !item.exclude && (!item.porcentajeAdelanto || (item.porcentajeAdelanto < 1 || item.porcentajeAdelanto > 100)))) this.errors.push('El porcentaje de adelanto debe ser entre 1 y 100')

            if (this.errors.length > 0) return

            const payload = {
                relations: this.relatedClientFactoringCompanies.map(item => ({
                    clientId: item.clientId,
                    creditLineId: item.creditLineId,
                    reservedFolioId: item.reservedFolioId,
                    factoringCompanyId: item.factoringCompanyId,
                    plazoAcordado: item.plazoAcordado,
                    tasaAdicional: item.tasaAdicional,
                    multiplicadorMoratorio: item.multiplicadorMoratorio,
                    porcentajeAdelanto: item.porcentajeAdelanto,
                    exclude: item.exclude,
                    active: item.active
                })),
                ...{ factoringCompanyId: this.companyId }
            }
            const response = await this.processActionCM2('/client-factoring-company', payload, false, false)
            if (!response || response instanceof Error) return

            this.$swal({
                title: 'Relación entre clientes y empresa guardada',
                position: 'top',
                icon: 'success',
                toast: true,
                timer: 3000,
                scrollbarPadding: false,
                showConfirmButton: false,
                showCancelButton: false,
                showCloseButton: false
            })
            await this.setRelations()
            this.gotDirty(false)
        }
    },

    computed: {
        fullAdministration () {
            return this.Utils.booleanValue(process.env.VUE_APP_FULL_COMMERCIAL_RELATIONS)
        },

        record () {
            if (!this.factoringCompany) return {}
            return this.factoringCompany
        },

        isCreditLine () {
            return !!this.creditLine
        },

        editField () {
            return !this.record.editing
        },

        availableClients () {
            return this.clients.filter(client => {
                return !(this.relatedClientFactoringCompanies || []).some(selected => selected.clientId === client.clientId)
            })
        }
    },

    watch: {
        relatedClientFactoringCompanies: {
            deep: true,
            handler (_newValue, oldValue) {
                if (oldValue) this.gotDirty(true)
            }
        }
    }
}
</script>
<style scoped>
    div.multiple-labels-container {
        display: flex;
        align-items: center;
    }

    div.multiple-labels {
        display: inline-grid;
    }

    div.multiple-labels .ribbon.label {
        display: block;
    }

    div.multiple-labels .ribbon.label:first-child {
        margin-top: 0rem;
    }

    div.multiple-labels .ribbon.label:not(:first-child) {
        margin-top: 0.3rem;
    }

    .full-input {
        width: 100%!important;
    }
    .mb-10 {
        margin-bottom: 10px !important;
    }
</style>
